import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave, faUser, faLock, faBell, faShieldAlt, faSpinner, faCheckCircle,
  faExclamationTriangle, faTrash, faCrown, faEnvelope, faMobileAlt, faDesktop,
  faMoon, faBox, faComment, faAt, faCopy, faChartLine, faShield, faCalendarWeek,
  faBullhorn, faPalette, faSun, faGlobe, faRobot, faInfoCircle, faPlay, faHistory,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { userAPI, currencyAPI, notificationSettingsAPI } from '../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { logout, updateUserCurrency, updateUserProfile } from '../store/slices/authSlice';
import TwoFactorSettings from '../components/TwoFactorSettings';
import { trackFeature } from '../services/telemetryService';
import { useTheme } from '../hooks/useTheme';
import { useI18n, LANGUAGES } from '../hooks/useI18n';
import { setTelemetryOptOut } from '../services/telemetryService';
import '../styles/Settings.css';

interface NotificationSettings {
  id?: string;
  itemUpdates: boolean;
  comments: boolean;
  mentions: boolean;
  duplicateDetection: boolean;
  valuePredictions: boolean;
  securityAlerts: boolean;
  weeklyDigest: boolean;
  marketingEmails: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  inAppNotifications: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
}

const defaultNotifSettings: NotificationSettings = {
  itemUpdates: true,
  comments: true,
  mentions: true,
  duplicateDetection: true,
  valuePredictions: true,
  securityAlerts: true,
  weeklyDigest: false,
  marketingEmails: false,
  emailNotifications: true,
  pushNotifications: true,
  inAppNotifications: true,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
};

const mapBackendToUI = (data: any): NotificationSettings => ({
  ...defaultNotifSettings,
  itemUpdates: data.itemUpdates ?? defaultNotifSettings.itemUpdates,
  comments: data.comments ?? defaultNotifSettings.comments,
  mentions: data.mentions ?? defaultNotifSettings.mentions,
  duplicateDetection: data.duplicateAlerts ?? data.duplicateDetection ?? defaultNotifSettings.duplicateDetection,
  valuePredictions: data.valueChanges ?? data.valuePredictions ?? defaultNotifSettings.valuePredictions,
  securityAlerts: data.securityAlerts ?? defaultNotifSettings.securityAlerts,
  weeklyDigest: data.weeklyDigest ?? defaultNotifSettings.weeklyDigest,
  marketingEmails: data.marketingEmails ?? defaultNotifSettings.marketingEmails,
  emailNotifications: data.emailEnabled ?? data.emailNotifications ?? defaultNotifSettings.emailNotifications,
  pushNotifications: data.pushEnabled ?? data.pushNotifications ?? defaultNotifSettings.pushNotifications,
  inAppNotifications: data.inAppEnabled ?? data.inAppNotifications ?? defaultNotifSettings.inAppNotifications,
  quietHoursEnabled: data.quietHoursEnabled ?? defaultNotifSettings.quietHoursEnabled,
  quietHoursStart: data.quietHoursStart ?? defaultNotifSettings.quietHoursStart,
  quietHoursEnd: data.quietHoursEnd ?? defaultNotifSettings.quietHoursEnd,
});

const mapUIToBackend = (settings: NotificationSettings) => ({
  itemUpdates: settings.itemUpdates,
  comments: settings.comments,
  mentions: settings.mentions,
  duplicateAlerts: settings.duplicateDetection,
  valueChanges: settings.valuePredictions,
  securityAlerts: settings.securityAlerts,
  weeklyDigest: settings.weeklyDigest,
  marketingEmails: settings.marketingEmails,
  emailEnabled: settings.emailNotifications,
  pushEnabled: settings.pushNotifications,
  inAppEnabled: settings.inAppNotifications,
  quietHoursEnabled: settings.quietHoursEnabled,
  quietHoursStart: settings.quietHoursStart,
  quietHoursEnd: settings.quietHoursEnd,
});

const Settings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { theme, setTheme } = useTheme();
  const { t, language, setLanguage } = useI18n();
  const [activeTab, setActiveTab] = useState('profile');
  const [autoLogoutSetting, setAutoLogoutSetting] = useState<string>(
    () => localStorage.getItem('inactivity_timeout') || '1800000'
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [currencies, setCurrencies] = useState<Array<{ code: string; name: string; symbol: string }>>([]);
  const [defaultCurrency, setDefaultCurrency] = useState(user?.defaultCurrency || 'USD');

  // Email change
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailChanging, setEmailChanging] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    bio: '',
    role: 'MEMBER',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    notifications: true,
    emailUpdates: true,
    twoFactor: false,
  });

  // Notification settings
  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(defaultNotifSettings);
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifHasChanges, setNotifHasChanges] = useState(false);

  // Accessibility preferences
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem('reduceMotion') === 'true');
  const [prefersTouch, setPrefersTouch] = useState(() => localStorage.getItem('prefersTouch') === 'true');

  // Diagnostics preference (telemetry opt-out)
  const [diagnosticsEnabled, setDiagnosticsEnabled] = useState(() => localStorage.getItem('__eq_prefs_dx') !== '1');

  const fetchProfile = useCallback(async () => {
    try {
      const response = await userAPI.getProfile();
      const user = response.data.user || response.data;
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        bio: user.bio || '',
        role: user.role || 'MEMBER',
        twoFactor: user.twoFactorEnabled || false,
      }));

      // Fetch default currency
      if (user.defaultCurrency) {
        setDefaultCurrency(user.defaultCurrency);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      const status = (error as any)?.response?.status;
      const msg = (error as any)?.response?.data?.error;
      if (status === 401 || status === 404) {
        // Token invalid or user missing (e.g., DB reset). Force logout to refresh token/user.
        showMessage('error', msg || 'Session expired. Please log in again.');
        setTimeout(() => {
          dispatch(logout());
          navigate('/login');
        }, 800);
      } else {
        showMessage('error', 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  useEffect(() => {
    fetchProfile();
    
    // Fetch supported currencies
    const fetchCurrencies = async () => {
      try {
        const response = await currencyAPI.getSupportedCurrencies();
        setCurrencies(response.data);
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
      }
    };
    fetchCurrencies();

    // Fetch notification settings
    const fetchNotifSettings = async () => {
      try {
        setNotifLoading(true);
        const response = await notificationSettingsAPI.get();
        if (response.data) {
          setNotifSettings(mapBackendToUI(response.data));
        }
      } catch (err: any) {
        console.log('Using default notification settings');
      } finally {
        setNotifLoading(false);
      }
    };
    fetchNotifSettings();
  }, [fetchProfile]);

  // Sync defaultCurrency with Redux user state when it changes
  useEffect(() => {
    if (user?.defaultCurrency) {
      setDefaultCurrency(user.defaultCurrency);
    }
  }, [user?.defaultCurrency]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await userAPI.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        bio: formData.bio,
      });
      dispatch(updateUserProfile({ firstName: formData.firstName, lastName: formData.lastName }));
      showMessage('success', 'Profile updated successfully!');
      trackFeature('profile_updated');
    } catch (error) {
      console.error('Failed to update profile:', error);
      showMessage('error', 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.currentPassword || !formData.newPassword) {
      showMessage('error', 'Please fill in all password fields');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    if (formData.newPassword.length < 6) {
      showMessage('error', 'New password must be at least 6 characters');
      return;
    }
    try {
      setSaving(true);
      await userAPI.changePassword(formData.currentPassword, formData.newPassword);
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      showMessage('success', 'Password changed successfully!');
      trackFeature('password_changed');
    } catch (error: unknown) {
      console.error('Failed to change password:', error);
      const err = error as { response?: { data?: { error?: string } } };
      showMessage('error', err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyChange = async (newCurrency: string) => {
    try {
      setSaving(true);
      await currencyAPI.updateUserDefault(newCurrency);
      setDefaultCurrency(newCurrency);
      dispatch(updateUserCurrency(newCurrency));
      showMessage('success', 'Default currency updated successfully!');
    } catch (error) {
      console.error('Failed to update currency:', error);
      showMessage('error', 'Failed to update currency');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(true);
  };

  // --- Email Change handler ---
  const handleEmailChange = async () => {
    if (!newEmail || !emailPassword) {
      showMessage('error', 'Please enter new email and your current password');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      showMessage('error', 'Please enter a valid email address');
      return;
    }
    try {
      setEmailChanging(true);
      await userAPI.changeEmail(newEmail, emailPassword);
      setFormData(prev => ({ ...prev, email: newEmail }));
      setShowEmailChange(false);
      setNewEmail('');
      setEmailPassword('');
      showMessage('success', 'Email address updated successfully!');
    } catch (error: any) {
      console.error('Failed to change email:', error);
      showMessage('error', error.response?.data?.error || 'Failed to change email');
    } finally {
      setEmailChanging(false);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!deletePassword) {
      showMessage('error', 'Please enter your password to confirm');
      return;
    }

    try {
      setSaving(true);
      await userAPI.deleteAccount(deletePassword);
      showMessage('success', 'Account deleted successfully. Redirecting...');
      trackFeature('account_deleted');
      setTimeout(() => {
        dispatch(logout());
        navigate('/login');
      }, 1500);
    } catch (error: unknown) {
      console.error('Failed to delete account:', error);
      const err = error as { response?: { data?: { error?: string } } };
      showMessage('error', err.response?.data?.error || 'Failed to delete account. Please check your password.');
      setSaving(false);
    }
  };

  // --- Notification handlers ---
  const handleNotifToggle = (key: keyof NotificationSettings) => {
    setNotifSettings(prev => ({ ...prev, [key]: !prev[key] }));
    setNotifHasChanges(true);
  };

  const handleNotifTimeChange = (key: 'quietHoursStart' | 'quietHoursEnd', value: string) => {
    setNotifSettings(prev => ({ ...prev, [key]: value }));
    setNotifHasChanges(true);
  };

  const handleNotifSave = async () => {
    try {
      setNotifSaving(true);
      await notificationSettingsAPI.update(mapUIToBackend(notifSettings));
      setNotifHasChanges(false);
      showMessage('success', 'Notification settings saved!');
    } catch (err: any) {
      console.error('Failed to save notification settings:', err);
      showMessage('error', 'Failed to save notification settings');
    } finally {
      setNotifSaving(false);
    }
  };

  // --- Accessibility handlers ---
  const handleReduceMotionToggle = () => {
    const next = !reduceMotion;
    setReduceMotion(next);
    localStorage.setItem('reduceMotion', String(next));
    document.body.classList.toggle('reduce-motion', next);
  };

  const handlePrefersTouchToggle = () => {
    const next = !prefersTouch;
    setPrefersTouch(next);
    localStorage.setItem('prefersTouch', String(next));
    document.body.classList.toggle('prefer-touch', next);
  };

  // Apply saved accessibility prefs on mount
  useEffect(() => {
    if (reduceMotion) document.body.classList.add('reduce-motion');
    if (prefersTouch) document.body.classList.add('prefer-touch');
    return () => {
      // don't remove on unmount — keep active across app
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePassword('');
  };

  // Toggle component for reuse – NO native checkbox (eliminates ghost circle)
  const Toggle = ({ checked, onChange, label, description, icon }: {
    checked: boolean;
    onChange: () => void;
    label: string;
    description: string;
    icon?: any;
  }) => (
    <div className="toggle-item" role="switch" aria-checked={checked} tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(); } }}
    >
      <div className="toggle-info">
        {icon && <FontAwesomeIcon icon={icon} className="toggle-icon" />}
        <p>{label}</p>
        <span>{description}</span>
      </div>
      <div
        className={`toggle-switch${checked ? ' checked' : ''}`}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onChange(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(); } }}
      >
        <span className="toggle-slider"></span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="settings-loading">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading settings...
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="settings-header">
        <h2>{t('settings.title')}</h2>
        <p>{t('settings.subtitle')}</p>
      </div>

      <div className="settings-container">
        {/* Tabs */}
        <div className="settings-tabs">
          <button className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
            <FontAwesomeIcon icon={faUser} /> {t('settings.general')}
          </button>
          <button className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>
            <FontAwesomeIcon icon={faLock} /> {t('settings.security')}
          </button>
          <button className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveTab('notifications')}>
            <FontAwesomeIcon icon={faBell} /> {t('nav.notifications')}
          </button>
          <button className={`tab-btn ${activeTab === 'accessibility' ? 'active' : ''}`} onClick={() => setActiveTab('accessibility')}>
            <FontAwesomeIcon icon={faMoon} /> {t('settings.accessibility')}
          </button>
          <button className={`tab-btn ${activeTab === 'appearance' ? 'active' : ''}`} onClick={() => setActiveTab('appearance')}>
            <FontAwesomeIcon icon={faPalette} /> {t('settings.appearance')}
          </button>
          <button className={`tab-btn ${activeTab === 'language' ? 'active' : ''}`} onClick={() => setActiveTab('language')}>
            <FontAwesomeIcon icon={faGlobe} /> {t('settings.language')}
          </button>
          <button className={`tab-btn ${activeTab === 'privacy' ? 'active' : ''}`} onClick={() => setActiveTab('privacy')}>
            <FontAwesomeIcon icon={faShieldAlt} /> {t('settings.privacyTab')}
          </button>
          <button className={`tab-btn ${activeTab === 'ai' ? 'active' : ''}`} onClick={() => setActiveTab('ai')}>
            <FontAwesomeIcon icon={faRobot} /> {t('settings.aiTab')}
          </button>
          <button className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`} onClick={() => setActiveTab('about')}>
            <FontAwesomeIcon icon={faInfoCircle} /> {t('settings.aboutTab' as any)}
          </button>
        </div>

        {/* Tab Contents */}
        <div className="settings-content">
          {/* Message Toast */}
          {message && (
            <div className={`settings-message ${message.type}`}>
              <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle} />
              {message.text}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="settings-panel">
              <h3>{t('settings.profileTitle')}</h3>
              
              {/* Initials Avatar */}
              <div className="profile-picture-section">
                <div className="initials-avatar-large">
                  {(formData.firstName?.[0] || '').toUpperCase()}{(formData.lastName?.[0] || '').toUpperCase()}
                </div>
              </div>

              {/* Role Badge */}
              <div className="role-badge-container">
                <div className={`role-badge role-${formData.role.toLowerCase()}`}>
                  <FontAwesomeIcon icon={formData.role === 'ADMIN' || formData.role === 'OWNER' ? faCrown : faUser} />
                  <span>{t(`role.${formData.role}`) || formData.role}</span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('settings.firstName')}</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t('settings.firstNamePlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.lastName')}</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t('settings.lastNamePlaceholder')}
                  />
                </div>
              </div>

              {/* Email with change option */}
              <div className="form-group">
                <label>{t('settings.emailAddress')}</label>
                <div className="email-display-row">
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="email-readonly"
                  />
                  <button
                    className="btn-change-email"
                    onClick={() => setShowEmailChange(!showEmailChange)}
                  >
                    <FontAwesomeIcon icon={faEnvelope} /> {t('settings.changeEmail')}
                  </button>
                </div>
                {showEmailChange && (
                  <div className="email-change-form">
                    <div className="form-group">
                      <label>{t('settings.newEmailAddress')}</label>
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder={t('settings.enterNewEmail')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('settings.currentPassword')}</label>
                      <input
                        type="password"
                        value={emailPassword}
                        onChange={(e) => setEmailPassword(e.target.value)}
                        placeholder={t('settings.currentPasswordConfirm')}
                      />
                    </div>
                    <div className="email-change-actions">
                      <button className="btn-cancel" onClick={() => { setShowEmailChange(false); setNewEmail(''); setEmailPassword(''); }}>
                        {t('action.cancel')}
                      </button>
                      <button className="btn-save" onClick={handleEmailChange} disabled={emailChanging || !newEmail || !emailPassword}>
                        <FontAwesomeIcon icon={emailChanging ? faSpinner : faSave} spin={emailChanging} />
                        {emailChanging ? t('settings.updatingEmail') : t('settings.updateEmail')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{t('settings.bio')}</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder={t('settings.bioPlaceholder')}
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>{t('settings.defaultCurrency')}</label>
                <select
                  value={defaultCurrency}
                  onChange={(e) => handleCurrencyChange(e.target.value)}
                  disabled={saving}
                >
                  {currencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
                <p className="form-hint">{t('settings.currencyHint')}</p>
              </div>
              
              <button className="btn-save" onClick={handleSaveProfile} disabled={saving}>
                <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
                {saving ? t('settings.savingChanges') : t('action.saveChanges')}
              </button>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="settings-panel">
              <h3>{t('settings.securityTitle')}</h3>
              <div className="security-section">
                <h4>{t('settings.changePassword')}</h4>
                <div className="form-group">
                  <label>{t('settings.currentPassword')}</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    placeholder={t('settings.enterCurrentPassword')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.newPassword')}</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder={t('settings.enterNewPassword')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('settings.confirmPassword')}</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder={t('settings.confirmNewPassword')}
                  />
                </div>
                <button className="btn-save" onClick={handleChangePassword} disabled={saving}>
                  <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
                  {saving ? t('settings.updatingPassword') : t('settings.changePassword')}
                </button>
              </div>

              <div className="security-section">
                <TwoFactorSettings />
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="settings-panel">
              <h3>{t('settings.notifPreferences')}</h3>

              {notifLoading ? (
                <div className="settings-loading" style={{ height: 200 }}>
                  <FontAwesomeIcon icon={faSpinner} spin /> {t('settings.loadingNotifications')}
                </div>
              ) : (
                <>
                  {/* Notification Types */}
                  <div className="settings-section-group">
                    <h4 className="section-subtitle">
                      <FontAwesomeIcon icon={faBell} /> {t('settings.notifTypes')}
                    </h4>
                    <Toggle checked={notifSettings.itemUpdates} onChange={() => handleNotifToggle('itemUpdates')}
                      icon={faBox} label={t('settings.notifItemUpdates')} description={t('settings.notifItemUpdatesDesc')} />
                    <Toggle checked={notifSettings.comments} onChange={() => handleNotifToggle('comments')}
                      icon={faComment} label={t('settings.notifComments')} description={t('settings.notifCommentsDesc')} />
                    <Toggle checked={notifSettings.mentions} onChange={() => handleNotifToggle('mentions')}
                      icon={faAt} label={t('settings.notifMentions')} description={t('settings.notifMentionsDesc')} />
                    <Toggle checked={notifSettings.duplicateDetection} onChange={() => handleNotifToggle('duplicateDetection')}
                      icon={faCopy} label={t('settings.notifDuplicates')} description={t('settings.notifDuplicatesDesc')} />
                    <Toggle checked={notifSettings.valuePredictions} onChange={() => handleNotifToggle('valuePredictions')}
                      icon={faChartLine} label={t('settings.notifValuePredictions')} description={t('settings.notifValuePredictionsDesc')} />
                    <Toggle checked={notifSettings.securityAlerts} onChange={() => handleNotifToggle('securityAlerts')}
                      icon={faShield} label={t('settings.notifSecurityAlerts')} description={t('settings.notifSecurityAlertsDesc')} />
                  </div>

                  {/* Email Preferences */}
                  <div className="settings-section-group">
                    <h4 className="section-subtitle">
                      <FontAwesomeIcon icon={faEnvelope} /> {t('settings.notifEmailPrefs')}
                    </h4>
                    <Toggle checked={notifSettings.weeklyDigest} onChange={() => handleNotifToggle('weeklyDigest')}
                      icon={faCalendarWeek} label={t('settings.notifWeeklyDigest')} description={t('settings.notifWeeklyDigestDesc')} />
                    <Toggle checked={notifSettings.marketingEmails} onChange={() => handleNotifToggle('marketingEmails')}
                      icon={faBullhorn} label={t('settings.notifMarketing')} description={t('settings.notifMarketingDesc')} />
                  </div>

                  {/* Delivery Channels */}
                  <div className="settings-section-group">
                    <h4 className="section-subtitle">
                      <FontAwesomeIcon icon={faDesktop} /> {t('settings.notifChannels')}
                    </h4>
                    <Toggle checked={notifSettings.emailNotifications} onChange={() => handleNotifToggle('emailNotifications')}
                      icon={faEnvelope} label={t('settings.notifEmail')} description={t('settings.notifEmailDesc')} />
                    <Toggle checked={notifSettings.pushNotifications} onChange={() => handleNotifToggle('pushNotifications')}
                      icon={faMobileAlt} label={t('settings.notifPush')} description={t('settings.notifPushDesc')} />
                    <Toggle checked={notifSettings.inAppNotifications} onChange={() => handleNotifToggle('inAppNotifications')}
                      icon={faDesktop} label={t('settings.notifInApp')} description={t('settings.notifInAppDesc')} />
                  </div>

                  {/* Quiet Hours */}
                  <div className="settings-section-group">
                    <h4 className="section-subtitle">
                      <FontAwesomeIcon icon={faMoon} /> {t('settings.notifQuietHours')}
                    </h4>
                    <Toggle checked={notifSettings.quietHoursEnabled} onChange={() => handleNotifToggle('quietHoursEnabled')}
                      icon={faMoon} label={t('settings.notifEnableQuietHours')} description={t('settings.notifQuietHoursDesc')} />

                    {notifSettings.quietHoursEnabled && (
                      <div className="quiet-hours-range">
                        <div className="form-group">
                          <label>{t('settings.notifStartTime')}</label>
                          <input type="time" value={notifSettings.quietHoursStart || '22:00'}
                            onChange={(e) => handleNotifTimeChange('quietHoursStart', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label>{t('settings.notifEndTime')}</label>
                          <input type="time" value={notifSettings.quietHoursEnd || '08:00'}
                            onChange={(e) => handleNotifTimeChange('quietHoursEnd', e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>

                  {notifHasChanges && (
                    <button className="btn-save" onClick={handleNotifSave} disabled={notifSaving}>
                      <FontAwesomeIcon icon={notifSaving ? faSpinner : faSave} spin={notifSaving} />
                      {notifSaving ? t('settings.notifSaving') : t('settings.notifSaveBtn')}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Accessibility Tab */}
          {activeTab === 'accessibility' && (
            <div className="settings-panel">
              <h3>{t('settings.accessibilityTitle')}</h3>
              <p className="section-description">
                {t('settings.accessibilityDesc')}
              </p>

              <div className="settings-section-group">
                <h4 className="section-subtitle">
                  <FontAwesomeIcon icon={faMoon} /> {t('settings.motionAnimations')}
                </h4>
                <Toggle checked={reduceMotion} onChange={handleReduceMotionToggle}
                  icon={faMoon} label={t('settings.reduceMotion')}
                  description={t('settings.reduceMotionDesc')} />
                <p className="form-hint" style={{ marginTop: -8, marginBottom: 16 }}>
                  {t('settings.reduceMotionHint')}
                </p>
              </div>

              <div className="settings-section-group">
                <h4 className="section-subtitle">
                  <FontAwesomeIcon icon={faMobileAlt} /> {t('settings.touchMobile')}
                </h4>
                <Toggle checked={prefersTouch} onChange={handlePrefersTouchToggle}
                  icon={faMobileAlt} label={t('settings.touchFriendly')}
                  description={t('settings.touchFriendlyDesc')} />
                <p className="form-hint" style={{ marginTop: -8, marginBottom: 16 }}>
                  {t('settings.touchFriendlyHint')}
                </p>
              </div>

              <div className="settings-section-group" style={{ background: theme === 'dark' ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '16px 18px', marginTop: 8 }}>
                <h4 className="section-subtitle" style={{ color: '#f59e0b', marginBottom: 8 }}>
                  <FontAwesomeIcon icon={faInfoCircle} /> {t('settings.mobileWarningTitle')}
                </h4>
                <p className="form-hint" style={{ marginBottom: 6, lineHeight: 1.55 }}>
                  {t('settings.mobileWarningText')}
                </p>
                <p className="form-hint" style={{ marginBottom: 0, fontStyle: 'italic', opacity: 0.85 }}>
                  {theme === 'dark' ? t('settings.mobileWarningDarkHint') : t('settings.mobileWarningLightHint')}
                </p>
              </div>
            </div>
          )}

          {/* Appearance Tab — Theme only */}
          {activeTab === 'appearance' && (
            <div className="settings-panel">
              <h3>{t('settings.appearance')}</h3>

              {/* Theme Switcher */}
              <div className="settings-section-group">
                <h4 className="section-subtitle">
                  <FontAwesomeIcon icon={faPalette} /> {t('settings.theme')}
                </h4>
                <p className="section-description">{t('settings.themeDesc')}</p>

                <div className="theme-switcher">
                  <button
                    className={`theme-option${theme === 'light' ? ' active' : ''}`}
                    onClick={() => setTheme('light')}
                    aria-pressed={theme === 'light'}
                  >
                    <FontAwesomeIcon icon={faSun} />
                    <span>{t('settings.lightTheme')}</span>
                  </button>
                  <button
                    className={`theme-option${theme === 'dark' ? ' active' : ''}`}
                    onClick={() => setTheme('dark')}
                    aria-pressed={theme === 'dark'}
                  >
                    <FontAwesomeIcon icon={faMoon} />
                    <span>{t('settings.darkTheme')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Language Tab */}
          {activeTab === 'language' && (
            <div className="settings-panel">
              <h3>{t('settings.language')}</h3>

              <div className="settings-section-group">
                <h4 className="section-subtitle">
                  <FontAwesomeIcon icon={faGlobe} /> {t('settings.language')}
                </h4>
                <p className="section-description">{t('settings.languageDesc')}</p>
                <p className="form-hint">Click a language card to change your app language.</p>

                <div className="language-selector">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      className={`language-option${language === lang.code ? ' active' : ''}`}
                      onClick={() => setLanguage(lang.code)}
                      aria-pressed={language === lang.code}
                    >
                      <span className="lang-flag">{lang.flag}</span>
                      <span className="lang-label">{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="settings-panel">
              <h3>{t('settings.privacyTitle')}</h3>
              <div className="privacy-info">
                <h4>{t('settings.yourData')}</h4>
                <p>{t('settings.yourDataDesc')}</p>
              </div>
              
              <div className="danger-zone">
                <h4>{t('settings.dangerZone')}</h4>
                <p>{t('settings.dangerZoneDesc')}</p>
                
                {!showDeleteConfirm ? (
                  <button className="btn-delete-account" onClick={handleDeleteAccount} disabled={saving}>
                    <FontAwesomeIcon icon={faTrash} />
                    {t('settings.deleteAccount')}
                  </button>
                ) : (
                  <div className="delete-confirm-box">
                    <p><strong>{t('settings.deleteConfirmTitle')}</strong></p>
                    <p>{t('settings.deleteConfirmDesc')}</p>
                    <input
                      type="password"
                      placeholder={t('settings.enterPassword')}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      autoFocus
                    />
                    <div className="delete-actions">
                      <button className="btn-cancel" onClick={cancelDelete} disabled={saving}>
                        {t('action.cancel')}
                      </button>
                      <button className="btn-confirm-delete" onClick={confirmDeleteAccount} disabled={saving || !deletePassword}>
                        <FontAwesomeIcon icon={saving ? faSpinner : faTrash} spin={saving} />
                        {saving ? t('settings.deleting') : t('settings.confirmDeleteBtn')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="privacy-info">
                <h4>{t('settings.privacyPolicy')}</h4>
                <p>{t('settings.privacyPolicyDesc')}</p>
                <button className="btn-link" onClick={() => navigate('/privacy')}>{t('settings.viewPrivacyPolicy')}</button>
              </div>

              {/* Service diagnostics — intentionally minimal UI */}
              <div className="privacy-info" style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color, rgba(255,255,255,0.04))' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h4 style={{ fontSize: '13px', color: 'var(--text-muted, #94a3b8)', opacity: 0.7, fontWeight: 400, marginBottom: '2px' }}>Service diagnostics</h4>
                    <p style={{ fontSize: '11px', color: 'var(--text-muted, #94a3b8)', opacity: 0.5, margin: 0 }}>Help improve stability by sharing anonymous usage data</p>
                  </div>
                  <div
                    className="toggle-item"
                    role="switch"
                    aria-checked={diagnosticsEnabled}
                    tabIndex={0}
                    onClick={() => {
                      const newVal = !diagnosticsEnabled;
                      setDiagnosticsEnabled(newVal);
                      setTelemetryOptOut(!newVal);
                    }}
                    style={{ cursor: 'pointer', opacity: 0.55, transform: 'scale(0.8)' }}
                  >
                    <span className={`toggle-switch ${diagnosticsEnabled ? 'active' : ''}`}>
                      <span className="toggle-knob" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Provider Tab */}
          {activeTab === 'ai' && (
            <div className="settings-panel">
              <h3><FontAwesomeIcon icon={faRobot} /> {t('settings.aiTitle')}</h3>
              <p className="settings-description">
                {t('settings.aiDescription')}
              </p>

              <div className="info-banner" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px', background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.25)', borderRadius: '12px', color: '#6366f1' }}>
                <FontAwesomeIcon icon={faRobot} style={{ fontSize: '24px' }} />
                <div style={{ flex: 1 }}>
                  <strong style={{ display: 'block', marginBottom: '4px', fontSize: '15px' }}>{t('settings.aiActive')}</strong>
                  <span style={{ fontSize: '14px', opacity: 0.9 }}>{t('settings.aiActiveDesc')}</span>
                </div>
              </div>

              <div className="ai-features-list" style={{ marginTop: '24px' }}>
                <h4 style={{ fontSize: '15px', marginBottom: '12px', color: 'var(--text-primary)' }}>{t('settings.aiFeatures')}</h4>
                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize: '18px' }}>💬</span>
                    <span style={{ fontSize: '14px' }}><strong>{t('settings.aiAssistant')}</strong> — {t('settings.aiAssistantDesc')}</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize: '18px' }}>🔍</span>
                    <span style={{ fontSize: '14px' }}><strong>{t('settings.aiDuplicateDetection')}</strong> — {t('settings.aiDuplicateDetectionDesc')}</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize: '18px' }}>📈</span>
                    <span style={{ fontSize: '14px' }}><strong>{t('settings.aiValuePrediction')}</strong> — {t('settings.aiValuePredictionDesc')}</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize: '18px' }}>🏷️</span>
                    <span style={{ fontSize: '14px' }}><strong>{t('settings.aiSmartCategorization')}</strong> — {t('settings.aiSmartCategorizationDesc')}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
          {/* About Tab */}
          {activeTab === 'about' && (
            <div className="settings-panel">
              <h3><FontAwesomeIcon icon={faInfoCircle} /> {t('settings.aboutTab' as any)}</h3>

              {/* Version badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', marginBottom: '24px' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#fff', fontWeight: 700 }}>E</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)' }}>ELVOAQ</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>{t('about.version' as any)}: <strong>v1.0.0</strong> &nbsp;&middot;&nbsp; {t('about.releaseDate' as any)}: Feb 2026</div>
                </div>
              </div>

              {/* Replay Tutorial */}
              <div style={{ padding: '18px', background: 'var(--card-bg, rgba(255,255,255,0.03))', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '12px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FontAwesomeIcon icon={faPlay} style={{ color: '#667eea' }} />
                      {t('about.replayTutorial' as any)}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>{t('about.replayTutorialDesc' as any)}</div>
                  </div>
                  <button
                    className="settings-btn-secondary"
                    onClick={() => window.dispatchEvent(new Event('tutorial:replay'))}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <FontAwesomeIcon icon={faPlay} /> {t('about.replayTutorial' as any)}
                  </button>
                </div>
              </div>

              {/* Auto-logout */}
              <div style={{ padding: '18px', background: 'var(--card-bg, rgba(255,255,255,0.03))', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '12px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <FontAwesomeIcon icon={faShield} style={{ color: '#f59e0b' }} />
                  {t('about.autoLogout' as any)}
                </div>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' }}>{t('about.autoLogoutDesc' as any)}</p>
                <select
                  className="settings-select"
                  value={autoLogoutSetting}
                  onChange={(e) => {
                    setAutoLogoutSetting(e.target.value);
                    localStorage.setItem('inactivity_timeout', e.target.value);
                  }}
                >
                  <option value="900000">{t('about.timeout15' as any)}</option>
                  <option value="1800000">{t('about.timeout30' as any)}</option>
                  <option value="3600000">{t('about.timeout60' as any)}</option>
                  <option value="7200000">{t('about.timeout120' as any)}</option>
                  <option value="never">{t('about.timeoutNever' as any)}</option>
                </select>
              </div>

              {/* Changelog */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <FontAwesomeIcon icon={faHistory} style={{ color: '#22c55e' }} />
                  {t('about.changelog' as any)}
                </h4>
                {[
                  { version: 'v1.0.0', date: 'Feb 2026', color: '#6366f1', items: [
                    'Complete AI-powered item management platform',
                    'Item library with categories, favorites & collections',
                    'Collections with password-lock gates & hidden private folders',
                    'AI-powered duplicate detection & value prediction engine',
                    'AI chat assistant & smart reminders',
                    'Accounting & asset depreciation module',
                    'Full dark/light theme system',
                    'RBAC workspace management with teams',
                    'Interactive platform tutorial with spotlight guide',
                    'Configurable auto-logout timeout & math CAPTCHA',
                    'Comprehensive audit log with real-time monitoring',
                    'Multi-language support (EN, DE, CS, SK)',
                    'PayPal & payment integration',
                    'Feedback system & notification settings',
                  ] },
                ].map((rel) => (
                  <div key={rel.version} style={{ display: 'flex', gap: '14px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: rel.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{rel.version.replace('v','')}</div>
                      <div style={{ width: '2px', flex: 1, background: 'var(--border-color, rgba(255,255,255,0.08))', margin: '4px 0' }} />
                    </div>
                    <div style={{ flex: 1, paddingBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: rel.color }}>{rel.version}</span>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{rel.date}</span>
                      </div>
                      <ul style={{ margin: 0, padding: '0 0 0 16px', listStyle: 'disc' }}>
                        {rel.items.map((item, i) => (
                          <li key={i} style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '3px' }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              {/* Legal links */}
              <div style={{ padding: '18px', background: 'var(--card-bg, rgba(255,255,255,0.03))', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '12px' }}>
                <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '12px' }}>{t('about.legalDocuments' as any)}</div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', background: 'rgba(99,102,241,0.1)', color: '#6366f1', textDecoration: 'none', fontSize: '14px', fontWeight: 500 }}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} /> {t('about.viewPrivacy' as any)}
                  </a>
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', background: 'rgba(99,102,241,0.1)', color: '#6366f1', textDecoration: 'none', fontSize: '14px', fontWeight: 500 }}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} /> {t('about.viewTerms' as any)}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
